import { useState } from "react";
import {
  TrendingUp,
  Shield,
  CheckCircle,
  Briefcase,
  Coins,
  Gift,
  FileText,
  Heart,
} from "lucide-react";
import donorsHeroImage from "../../assets/15c31ccc85ccb2769a1151c8414fb518517376a0.png";
import DonorInquiryModal from "../DonorInquiryModal";

export default function DonorsPage() {
  const [showDonorModal, setShowDonorModal] = useState(false);
  const whyDonate = [
    {
      icon: <Shield className="h-10 w-10" />,
      title: "Ledger-Verified Transparency",
      description: "Every donation tracked with blockchain-grade transparency",
    },
    {
      icon: <TrendingUp className="h-10 w-10" />,
      title: "Verified SROI",
      description:
        "Measurable Social Return on Investment for your contributions",
    },
    {
      icon: <Coins className="h-10 w-10" />,
      title: "SIC Credits",
      description:
        "Earn Social Impact Credits for recognition and accountability",
    },
    {
      icon: <Gift className="h-10 w-10" />,
      title: "HDT Recognition Tokens",
      description: "Receive Hostel Development Tokens for your support",
    },
    {
      icon: <CheckCircle className="h-10 w-10" />,
      title: "No Misuse of Funds",
      description:
        "Smart contract-based fund utilization with complete oversight",
    },
    {
      icon: <FileText className="h-10 w-10" />,
      title: "All Donations Traceable",
      description: "Real-time tracking from donation to impact",
    },
  ];

  const dashboardFeatures = [
    "Donation ledger with complete transaction history",
    "Impact summary with beneficiary data",
    "Student stories and testimonials",
    "SIC credits minted and awarded",
    "HDT tokens received",
    "Compliance records and audit trails",
  ];

  const csrBenefits = [
    "Full project proposal templates",
    "Budget structures",
    "Transparent audit trails",
    "SROI impact reports",
    "Smart-contract based fund utilization checks",
    "Digital dashboards",
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[70vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={donorsHeroImage}
            alt="Donation and charity"
            className="h-full w-full object-cover object-[center_30%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto mb-10 mt-10 max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-5xl lg:text-6xl">
              Donors, CSR & Philanthropy
            </h1>
            <p className="text-2xl leading-relaxed text-gray-100">
              Transform lives through transparent, impactful giving. Every rupee
              tracked, every impact measured, every contribution recognized.
            </p>
          </div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Why Donate to JBF
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              Experience unprecedented transparency and accountability in your
              philanthropic journey.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {whyDonate.map(item => (
              <div
                key={item.title}
                className="rounded-xl border-l-4 border-[#D4A017] bg-[#FFF9F0] p-6 shadow-lg transition-all hover:shadow-xl"
              >
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                  {item.icon}
                </div>
                <h3 className="mb-3 text-xl text-[#7A1E1E]">{item.title}</h3>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donor Dashboard Features */}
      <section className="bg-[#FFF5E6] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Donor Dashboard Features
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              Access a comprehensive dashboard that gives you complete
              visibility into your impact.
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
            {dashboardFeatures.map(feature => (
              <div
                key={feature}
                className="flex items-start gap-4 rounded-lg bg-white p-6 shadow-md transition-all hover:shadow-lg"
              >
                <CheckCircle className="mt-1 h-6 w-6 flex-shrink-0 text-[#D4A017]" />
                <span className="text-gray-800">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR Partnerships */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-[#7A1E1E] sm:text-4xl">
              CSR Partnerships
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              Structured partnerships designed for corporate social
              responsibility compliance and impact.
            </p>
          </div>

          {/* Main Card */}
          <div className="mx-auto max-w-4xl rounded-2xl bg-[#FFF9F0] p-8 shadow-lg lg:p-12">
            {/* Icon */}
            <div className="mb-6 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-[#7A1E1E]">
                <Briefcase className="h-8 w-8 text-white" />
              </div>
            </div>

            {/* Title */}
            <h3 className="mb-4 text-center text-2xl font-bold text-[#7A1E1E] lg:text-3xl">
              What CSR Bodies Get
            </h3>

            {/* Introductory Text */}
            <p className="mb-8 text-center text-gray-700">
              We provide comprehensive CSR solutions with complete
              documentation, compliance support, and impact measurement
              frameworks.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {csrBenefits.map(benefit => (
                <div
                  key={benefit}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4"
                >
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#D4A017]">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-sm text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA - same Donor Inquiry modal as HomePage */}
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setShowDonorModal(true)}
                className="flex items-center gap-2 rounded-lg bg-[#7A1E1E] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#5A1515]"
              >
                <Heart className="h-5 w-5" />
                Submit Donor / CSR Inquiry
              </button>
            </div>
          </div>
        </div>
      </section>

      <DonorInquiryModal
        open={showDonorModal}
        onClose={() => setShowDonorModal(false)}
      />
    </div>
  );
}
